import DynamicSelect from "@/shared/components/DynamicSelect/DynamicSelect.vue";
import { DynamicSelectConfig } from "@/shared/components/DynamicSelect/model/DynamicSelectConfig";
import { DynamicSelectData } from "@/shared/components/DynamicSelect/model/DynamicSelectData";
import { FORM_VALIDATORS } from "@/shared/constants/FormConstants";
import { STYLE_CLASSES } from "@/shared/constants/StyleConstants";
import { cloneDeep } from "lodash";
import Vue from "vue";
import Component from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import {
  DASHBOARD_TEMPLATE_MESSAGES,
  DATA_TYPE,
  SPECIAL_FIELDS,
  SEE_BY_LIST
} from "../../constants/DashboardTemplatesConstants";
import { Field } from "../../models/Field";

@Component({
  name: "ConfigureGraphCollection",
  components: {
    DynamicSelect
  },
  computed: {}
})
export default class ConfigureGraphCollection extends Vue {
  @Prop({ required: true }) indexCollectionFilter!: number;
  @Prop({ required: true }) chartName!: string;
  @Prop({ required: true }) groupBy!: DynamicSelectData[];
  @Prop({ required: true }) seeBy!: DynamicSelectData[];
  @Prop({ required: true }) chartColor!: { hex: string } | string;
  @Prop({ required: true }) rangeSize!: number | null;
  @Prop({ required: true }) specialCaseInForms!: number;
  @Prop() collection?: { id: string; name: string; fields?: Field[] };

  localChartName = "";
  localGroupBy: DynamicSelectData[] = [];
  localSeeBy: DynamicSelectData[] = [];
  localChartColor = "#63c7e7";
  localRangeSize: number | null = null;
  showRangeSize = false;

  primaryBtnClass = STYLE_CLASSES.PRIMARY_BTN;
  textFieldClass = STYLE_CLASSES.TEXT_FIELD;
  requiredField = [
    FORM_VALIDATORS.REQUIRED_FIELD,
    FORM_VALIDATORS.NOT_BLANK_SPACES_VALIDATOR
  ];

  requiredFieldRange = [
    FORM_VALIDATORS.REQUIRED_FIELD,
    FORM_VALIDATORS.GREATER_THAN_ZERO
  ];

  groupByOptionsConfig: DynamicSelectConfig = {
    multiple: true,
    default: [],
    chips: true,
    notFound: false,
    listAll: [],
    filled: false,
    messageLabel: DASHBOARD_TEMPLATE_MESSAGES.GROUP_BY,
    hideDetails: false,
    attach: false,
    limit: 3,
    required: true
  };

  seeByOptionsConfig: DynamicSelectConfig = {
    multiple: false,
    default: [],
    chips: true,
    notFound: false,
    listAll: cloneDeep(SEE_BY_LIST),
    filled: false,
    messageLabel: DASHBOARD_TEMPLATE_MESSAGES.SEE_BY,
    hideDetails: false,
    attach: false,
    required: true
  };

  configChartForm = false;

  get normalizedChartColor(): string {
    return typeof this.chartColor === "string"
      ? this.chartColor
      : this.chartColor?.hex || "#63c7e7";
  }

  get showSeeBy(): boolean {
    return this.localGroupBy.some(
      groupBy => groupBy.type == DATA_TYPE.TIME_STAMP
    );
  }

  mounted() {
    this.localChartName = this.chartName;
    this.localGroupBy = cloneDeep(this.groupBy);
    this.localSeeBy = cloneDeep(this.seeBy);
    this.localRangeSize = this.rangeSize;
    this.localChartColor = this.normalizedChartColor;
    this.updateGroup();
    if (this.rangeSize) {
      this.showRangeSize = true;
    }
  }

  cancelConfigChart() {
    this.$emit("close");
  }
  saveConfigChart() {
    this.$emit("save", {
      chartName: this.localChartName,
      groupBy: this.localGroupBy,
      seeBy: this.showSeeBy ? this.localSeeBy : [],
      rangeSize: this.localRangeSize,
      chartColor: { hex: this.localChartColor }
    });
  }
  @Watch("collection", { deep: true })
  updateGroup() {
    const fields = this.collection?.fields;
    if (!fields?.length) {
      this.groupByOptionsConfig.listAll = [];
      return;
    }

    fields.forEach(field => {
      if ("showJustInGroupBy" in field && field.showJustInGroupBy === false) {
        return;
      }
      this.groupByOptionsConfig.listAll.push({
        id: field.field,
        name: field.visualField,
        type: field.type
      });
    });

    if (
      (this.collection?.id === "forms" || this.collection?.id === "services") &&
      this.specialCaseInForms
    ) {
      const excludeIds = SPECIAL_FIELDS;
      this.groupByOptionsConfig.listAll = this.groupByOptionsConfig.listAll.filter(
        item => !excludeIds.includes(item.id)
      );
    }
  }

  @Watch("localGroupBy")
  onLocalGroupByChange() {
    const length = this.localGroupBy.length;
    // Validaciones tempranas
    if (length === 0 || length === 3) {
      this.resetRangeSize();
      return;
    }

    // Contar tipos en una sola iteración
    const { numberCount, timeStampCount, firstType } = this.localGroupBy.reduce(
      (
        acc: { numberCount: number; timeStampCount: number; firstType: string },
        item,
        index
      ) => {
        if (index === 0) acc.firstType = item.type ?? "";
        if (item.type === DATA_TYPE.NUMBER || item.type === DATA_TYPE.TIME_UNIT)
          acc.numberCount++;
        else if (item.type === DATA_TYPE.TIME_STAMP) acc.timeStampCount++;
        return acc;
      },
      { numberCount: 0, timeStampCount: 0, firstType: "" }
    );

    // Validar primer tipo
    if (
      firstType !== DATA_TYPE.NUMBER &&
      firstType !== DATA_TYPE.TIME_UNIT &&
      firstType !== DATA_TYPE.TIME_STAMP
    ) {
      this.resetRangeSize();
      return;
    }

    // Casos inválidos
    if (
      numberCount === 0 ||
      numberCount === 3 ||
      (numberCount === 0 && timeStampCount === 2) ||
      (numberCount === 2 && timeStampCount === 1)
    ) {
      this.resetRangeSize();
      return;
    }

    // Casos válidos
    if (
      (numberCount === 1 && (timeStampCount === 1 || timeStampCount === 0)) ||
      (numberCount === 2 && timeStampCount === 0)
    ) {
      this.showRangeSize = true;
    }
  }

  private resetRangeSize() {
    this.showRangeSize = false;
    this.localRangeSize = null;
  }
}
